import Vue from "vue";
import Vuex from "vuex";
import Axios from "axios";
import LocalForage from "localforage";
import FuseeGeleeWebUsb from "./services/FuseeGeleeWebUsb";
import swal from "sweetalert";
import { promisfyReadFileAsArrayBuffer, validateArrayBufferSha1 } from "./util";
import {
  ADD_LOG_MESSAGE,
  CLEAR_DEVICE,
  CLEAR_LOG,
  FINISH_ACTIVE_PAYLOAD_LOADING,
  FINISH_CONNECTING_DEVICE,
  FINISH_RUN_EXPLOIT,
  SET_ACTIVE_PAYLOAD,
  SET_COMPATIBLE_BROWSER,
  SET_DEVICE,
  SET_PAYLOAD_DEFINITIONS,
  SET_PAYLOAD_DOWNLOAD_PROGRESS,
  SET_WRITE_PAYLOAD_PROGRESS,
  START_ACTIVE_PAYLOAD_LOADING,
  START_CONNECTING_DEVICE,
  START_RUN_EXPLOIT
} from "./constants/mutation-types";
import {
  LOG_ERROR,
  LOG_INFO,
  LOG_SUCCESS,
  LOG_WARNING
} from "./constants/log-message-types";
import { CONNECT_DEVICE, SELECT_PAYLOAD, RUN_EXPLOIT } from "./constants/steps";
Vue.use(Vuex);

export default new Vuex.Store({
  state: {
    compatibleBrowser: null,
    connectingDevice: false,
    device: null,
    deviceId: null,
    payloadDefinitions: null,
    activePayloadLoading: null,
    activePayload: {
      definition: null,
      arrayBuffer: null
    },
    payloadDownloadProgress: 0,
    runningExploit: false,
    writePayloadProgress: 0,
    exploitSuccess: null,
    log: []
  },
  mutations: {
    [SET_COMPATIBLE_BROWSER](state, compatible) {
      state.compatibleBrowser = compatible;
    },
    [START_CONNECTING_DEVICE](state) {
      state.connectingDevice = true;
    },
    [FINISH_CONNECTING_DEVICE](state) {
      state.connectingDevice = false;
    },
    [SET_DEVICE](state, { device, deviceId }) {
      state.device = device;
      state.deviceId = deviceId;
      state.writePayloadProgress = 0;
    },
    [CLEAR_DEVICE](state) {
      state.device = null;
      state.deviceId = null;
      state.writePayloadProgress = 0;
      state.exploitSuccess = false;
    },
    [SET_PAYLOAD_DEFINITIONS](state, payloadDefinitions) {
      state.payloadDefinitions = payloadDefinitions;
    },
    [START_ACTIVE_PAYLOAD_LOADING](state) {
      state.activePayloadLoading = true;
    },
    [FINISH_ACTIVE_PAYLOAD_LOADING](state) {
      state.activePayloadLoading = false;
    },
    [SET_ACTIVE_PAYLOAD](state, { definition, arrayBuffer }) {
      state.activePayload = { definition, arrayBuffer };
    },
    [SET_PAYLOAD_DOWNLOAD_PROGRESS](state, progress) {
      state.payloadDownloadProgress = progress;
    },
    [START_RUN_EXPLOIT](state) {
      state.runningExploit = true;
      state.exploitSuccess = null;
      state.writePayloadProgress = 0;
    },
    [FINISH_RUN_EXPLOIT](state, success = true) {
      state.runningExploit = false;
      state.exploitSuccess = success;
    },
    [SET_WRITE_PAYLOAD_PROGRESS](state, progress) {
      state.writePayloadProgress = progress;
    },
    [ADD_LOG_MESSAGE](state, { type = LOG_INFO, message }) {
      state.log.push({ type, message });
    },
    [CLEAR_LOG](state) {
      state.log = [];
      state.payloadDownloadProgress = 0;
      state.writePayloadProgress = 0;
    }
  },
  getters: {
    initialized: state => {
      return (
        state.compatibleBrowser !== null && state.payloadDefinitions !== null
      );
    },
    getPayloadDefinitionById: state => id => {
      return state.payloadDefinitions.find(
        payloadDefinition => payloadDefinition.id === id
      );
    },
    connectDeviceComplete: state => {
      return state.device !== null && state.deviceId !== null;
    },
    selectPayloadComplete: state => {
      return (
        !state.activePayloadLoading &&
        state.activePayload.definition !== null &&
        state.activePayload.arrayBuffer !== null
      );
    },
    runExploitComplete: state => {
      return state.exploitSuccess === true;
    },
    working: state => {
      return (
        state.connectingDevice === true ||
        state.activePayloadLoading === true ||
        state.runningExploit === true
      );
    },
    activeStep: (state, getters) => {
      if (!getters.connectDeviceComplete) {
        return CONNECT_DEVICE;
      }

      if (!getters.selectPayloadComplete) {
        return SELECT_PAYLOAD;
      }

      return RUN_EXPLOIT;
    }
  },
  actions: {
    async initialize({ commit, dispatch, state }) {
      try {
        commit(SET_COMPATIBLE_BROWSER, FuseeGeleeWebUsb.isSupportedByBrowser());
        await dispatch("fetchRemotePayloadDefinitions");
      } catch (e) {
        swal({
          title: "Failed To Initialize",
          text: e.message,
          icon: "error"
        });
      }

      FuseeGeleeWebUsb.onDeviceDisconnected(async () => {
        commit(ADD_LOG_MESSAGE, {
          type: LOG_WARNING,
          message: "DEVICE DISCONNECTED"
        });
        commit(CLEAR_DEVICE);
      });

      if (state.compatibleBrowser === false)
        return this.$router.replace({ name: "incompatible-browser" });
    },
    async fetchRemotePayloadDefinitions({ commit }) {
      try {
        const { data } = await Axios.get("/payloads/definitions.json");
        commit(SET_PAYLOAD_DEFINITIONS, data);
      } catch (e) {
        console.error(e);
        throw e;
      }
    },
    async requestDeviceAccess({ commit }) {
      commit(START_CONNECTING_DEVICE);
      commit(ADD_LOG_MESSAGE, {
        type: LOG_INFO,
        message: "REQUESTING DEVICE ACCESS"
      });
      try {
        const device = await FuseeGeleeWebUsb.requestDeviceAccess();
        commit(ADD_LOG_MESSAGE, {
          type: LOG_INFO,
          message: "ATTEMPTING TO OBTAIN DEVICE ID.."
        });
        const deviceId = await FuseeGeleeWebUsb.getDeviceId(device);

        commit(SET_DEVICE, { device, deviceId });
        commit(ADD_LOG_MESSAGE, {
          type: LOG_SUCCESS,
          message: `CONNECTED TO DEVICE ID: '${deviceId}'`
        });
      } catch (e) {
        commit(ADD_LOG_MESSAGE, {
          type: LOG_ERROR,
          message: e.message ? e.message.toUpperCase() : e
        });
      } finally {
        commit(FINISH_CONNECTING_DEVICE);
      }
    },
    async loadActivePayload({ commit, dispatch }, payloadDefinition) {
      try {
        commit(START_ACTIVE_PAYLOAD_LOADING);

        const PAYLOAD_ARRAY_BUFFER_CACHE_KEY = `PAYLOAD_DEFINITION:${
          payloadDefinition.id
        }`;

        let payloadArrayBuffer = await LocalForage.getItem(
          PAYLOAD_ARRAY_BUFFER_CACHE_KEY
        );

        if (payloadArrayBuffer === null) {
          payloadArrayBuffer = await dispatch(
            "fetchRemotePayload",
            payloadDefinition
          );
        } else {
          commit(ADD_LOG_MESSAGE, {
            type: LOG_SUCCESS,
            message: `LOADED PAYLOAD ${payloadDefinition.name} v${
              payloadDefinition.version
            } FROM CACHE`
          });
          commit(SET_PAYLOAD_DOWNLOAD_PROGRESS, 100);
        }

        commit(ADD_LOG_MESSAGE, {
          type: LOG_INFO,
          message: `VERIFYING PAYLOAD SHA1 ${payloadDefinition.sha1}`
        });

        const valid = validateArrayBufferSha1(
          payloadArrayBuffer,
          payloadDefinition.sha1
        );

        if (valid) {
          commit(ADD_LOG_MESSAGE, {
            type: LOG_SUCCESS,
            message: "PAYLOAD VERIFIED AND LOADED"
          });
          LocalForage.setItem(
            PAYLOAD_ARRAY_BUFFER_CACHE_KEY,
            payloadArrayBuffer
          );
          commit(SET_ACTIVE_PAYLOAD, {
            definition: payloadDefinition,
            arrayBuffer: payloadArrayBuffer
          });
        } else {
          commit(ADD_LOG_MESSAGE, {
            type: LOG_ERROR,
            message: "FAILED TO VERIFY PAYLOAD"
          });
        }
      } catch (e) {
        console.error(e);
        commit(ADD_LOG_MESSAGE, {
          type: LOG_ERROR,
          message: "AN ERROR OCCURRED WHILE LOADING PAYLOAD"
        });
      } finally {
        commit(FINISH_ACTIVE_PAYLOAD_LOADING);
      }
    },
    runExploit({ commit, state }) {
      commit(START_RUN_EXPLOIT);
      FuseeGeleeWebUsb.runExploit({
        device: state.device,
        payload: state.activePayload.arrayBuffer,
        logCallback: ({ message, type }) => {
          commit(ADD_LOG_MESSAGE, { message, type });
        },
        successCallback: () => {
          const beers = "🍻🍻🍻🍻🍻🍻🍻🍻🍻🍻";

          for (let i = 0; i < 5; i++) {
            commit(ADD_LOG_MESSAGE, { message: beers, type: LOG_SUCCESS });
          }

          commit(ADD_LOG_MESSAGE, {
            message: "EXPLOIT SUCCESS 😈",
            type: LOG_SUCCESS
          });

          commit(FINISH_RUN_EXPLOIT, true);

          swal({
            title: "Exploit Success! 😈",
            text: beers,
            icon: "success"
          });
        },
        errorCallback: error => {
          commit(ADD_LOG_MESSAGE, {
            type: LOG_ERROR,
            message: error.message.toUpperCase()
          });
          commit(FINISH_RUN_EXPLOIT, false);
        },
        payloadWriteProgressCallback: progress => {
          commit(SET_WRITE_PAYLOAD_PROGRESS, progress);
        }
      });
    },
    async fetchRemotePayload({ commit }, payloadDefinition) {
      try {
        commit(ADD_LOG_MESSAGE, {
          type: LOG_INFO,
          message: `FETCHING PAYLOAD ${payloadDefinition.name} v${
            payloadDefinition.version
          } FROM SERVER`
        });
        commit(SET_PAYLOAD_DOWNLOAD_PROGRESS, 0);
        const { data } = await Axios.get(
          `/payloads/${payloadDefinition.filename}`,
          {
            responseType: "blob",
            onDownloadProgress: progress => {
              commit(
                SET_PAYLOAD_DOWNLOAD_PROGRESS,
                Math.floor((progress.loaded * 100) / progress.total)
              );
            }
          }
        );
        commit(ADD_LOG_MESSAGE, {
          type: LOG_SUCCESS,
          message: `LOADED PAYLOAD ${payloadDefinition.name} v${
            payloadDefinition.version
          } FROM SERVER`
        });
        return await promisfyReadFileAsArrayBuffer(data);
      } catch (e) {
        console.error(e);
        commit(ADD_LOG_MESSAGE, {
          type: LOG_ERROR,
          message: "FAILED TO OBTAIN REMOTE PAYLOAD"
        });
      }
    }
  }
});
